import slackIcon from '~/assets/public/slack-icon.svg';
import microsoftTeamsIcon from '~/assets/public/microsoft-teams.svg';
import { Icon, Page } from '~/components';
import { useApi, useConfirmation, useSession, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle, useFeatures, useIsMounted } from '~/hooks';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import styled, { css } from 'styled-components';
import { colors, weights } from '~/styles';
import RemoveSlackConfirmation from './RemoveSlackConfirmation';
import RemoveMicrosoftTeamsConfirmation from './RemoveMicrosoftTeamsConfirmation';
import SlackError from './SlackError';
import SlackSettings from './SlackSettings';
import MicrosoftTeamsSettings from './MicrosoftTeamsSettings';
import { BambooIntegration } from './bamboohr';
import ExpensifyIntegration from './expensify/ExpensifyIntegration';
import QBOIntegration from './qbo/QBOIntegration';
import SageIntegration from './sage/SageIntegration';
import XeroIntegration from './xero/XeroIntegration';
import RipplingIntegration from './rippling/RipplingIntegration';
import QBDIntegration from './qbd/QBDIntegration';
import SalesforceIntegration from './salesforce/SalesforceIntegration';
import HubSpotIntegration from './hubspot/HubSpotIntegration';
import XeroExportIntegration from './xero-export/XeroExportIntegration';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1.625rem;
  border-bottom: solid 1px ${colors.grey10};

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const List = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -1.25rem -0.625rem;
  padding: 2rem 0;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 10.25rem;
  height: 9.375rem;
  margin: 1.25rem 0.625rem;
  background-color: ${colors.white};
  box-shadow: 0 0.1875rem 1rem ${colors.grey10};
  border: solid 0.125rem ${colors.white};
  border-radius: 0.3125rem;
  overflow: hidden;
`;

const Description = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img`
  width: 2.625rem;
  height: 2.625rem;
`;

const Name = styled.p`
  margin-top: 0.75rem;
  font-size: 0.875rem;
  font-weight: ${weights.bold};
`;

const Actions = styled.div`
  display: flex;
  height: 2rem;
  background-color: ${colors.grey10};
`;

const ActionStyle = css`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 2rem;
  padding: 0;
  color: ${colors.black};
  font-size: 0.75rem;
  font-weight: ${weights.normal};
  background-color: ${colors.grey5};
  border-radius: 0;

  &:not(:first-child) {
    margin-left: 1px;
  }

  &:hover {
    color: ${colors.black};
    background-color: ${colors.grey10};
  }
`;

const ActionAnchor = styled.a`
  ${ActionStyle}
`;

const ActionButton = styled.button`
  ${ActionStyle}
`;

const CSVIcon = styled(Icon)`
  height: 2.625rem;
`;

function SlackIntegration({ integration, onChange }) {
  const scopes = integration.scopes.join(',');
  const confirmation = useConfirmation();
  const { workspace } = useWorkspace();
  const history = useHistory();
  const { url } = useRouteMatch();

  const handleRemove = () => {
    confirmation.prompt((resolve) => (
      <RemoveSlackConfirmation
        onClose={resolve}
        onRemove={() => {
          if (typeof onChange === 'function') {
            onChange();
          }
          resolve(true);
        }}
      />
    ));
  };

  return (
    <Item>
      <Description>
        <Image src={slackIcon} />
        <Name>Slack</Name>
      </Description>
      {integration.isConnected ? (
        <Actions>
          <ActionButton onClick={() => history.push(`${url}/slack`)}>Settings</ActionButton>
          <ActionButton onClick={handleRemove}>Remove</ActionButton>
        </Actions>
      ) : (
        <Actions>
          <ActionAnchor
            href="https://help.ruddr.io/integrations/slack-integration-overview"
            target="_blank"
            rel="noopener noreferrer">
            Details
          </ActionAnchor>
          <ActionAnchor
            href={`https://slack.com/oauth/v2/authorize?client_id=${integration.clientId}&scope=${scopes}&state=${workspace.id}`}>
            Install
          </ActionAnchor>
        </Actions>
      )}
    </Item>
  );
}

function CSVImport() {
  const history = useHistory();
  const { url } = useRouteMatch();

  return (
    <Item>
      <Description>
        <CSVIcon icon="file-csv" size="2x" />
        <Name>CSV Import</Name>
      </Description>
      <Actions>
        <ActionButton onClick={() => history.push(`${url}/csv`)}>Import</ActionButton>
      </Actions>
    </Item>
  );
}

const MICROSOFT_365_SETUP_GUIDE_URL = 'https://help.ruddr.io/integrations/microsoft-365-integration-setup';

function MicrosoftIntegration({ integration, onChange }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const { isImpersonation } = useSession();
  const confirmation = useConfirmation();
  const history = useHistory();
  const { url } = useRouteMatch();

  const handleInstall = async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).integrations().microsoft.connection.getAuthorizeUri();
      window.location.href = data.authorizeUri;
    } catch (error) {
      toast.error(error.message || 'Microsoft 365 could not be connected. Please try again.');
    }
  };

  const handleRemove = () => {
    confirmation.prompt((resolve) => (
      <RemoveMicrosoftTeamsConfirmation
        onClose={resolve}
        onRemove={() => {
          if (typeof onChange === 'function') {
            onChange();
          }
          resolve(true);
        }}
      />
    ));
  };

  return (
    <Item data-testid="microsoft-integration">
      <Description>
        <Image src={microsoftTeamsIcon} />
        <Name>Microsoft 365</Name>
      </Description>
      {integration.isConnected ? (
        <Actions>
          <ActionButton onClick={() => history.push(`${url}/microsoft`)}>Settings</ActionButton>
          <ActionButton onClick={handleRemove}>Remove</ActionButton>
        </Actions>
      ) : (
        <Actions>
          <ActionAnchor href={MICROSOFT_365_SETUP_GUIDE_URL} target="_blank" rel="noopener noreferrer">
            Details
          </ActionAnchor>
          {!isImpersonation && <ActionButton onClick={handleInstall}>Install</ActionButton>}
        </Actions>
      )}
    </Item>
  );
}

function Integration(props) {
  switch (props.integration?.id) {
    case 'xero-export':
      return <XeroExportIntegration {...props} />;
    case 'bamboohr':
      return <BambooIntegration {...props} />;
    case 'expensify':
      return <ExpensifyIntegration {...props} />;
    case 'slack':
      return <SlackIntegration {...props} />;
    case 'microsoft':
      return <MicrosoftIntegration {...props} />;
    case 'qbo':
      return <QBOIntegration {...props} />;
    case 'sage':
      return <SageIntegration {...props} />;
    case 'xero':
      return <XeroIntegration {...props} />;
    case 'csv':
      return <CSVImport {...props} />;
    case 'qbd':
      return <QBDIntegration {...props} />;
    case 'salesforce':
      return <SalesforceIntegration {...props} />;
    case 'rippling':
      return <RipplingIntegration {...props} />;
    case 'hubspot':
      return <HubSpotIntegration {...props} />;

    default:
      return null;
  }
}

const IntegrationsList = ({ title, integrations, onChange }) => {
  if (integrations.length === 0) return null;

  return (
    <Section>
      <Page.Title>{title}</Page.Title>
      <List>
        {integrations.map((integration, i) => (
          <Integration key={`${integration.id}-${i}`} integration={integration} onChange={onChange} />
        ))}
      </List>
    </Section>
  );
};

export default function Integrations() {
  const documentTitle = useDocumentTitle('Integrations');

  const api = useApi();
  const { workspace } = useWorkspace();
  const history = useHistory();
  const { path, url } = useRouteMatch();
  const [integrations, setIntegrations] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const isMounted = useIsMounted();
  const features = useFeatures();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const error = params.get('error');

  const activeIntegrations = useMemo(
    () => (integrations ? _.filter(integrations, { isConnected: true }) : []),
    [integrations],
  );
  const availableIntegrations = useMemo(
    () =>
      integrations
        ? _.filter(
            integrations,
            (integration) =>
              !integration.isConnected &&
              (integration.id !== 'microsoft' || features.previews.includes('microsoft365')),
          )
        : [],
    [integrations, features],
  );

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).integrations().get();

      if (!isMounted.current) return;

      setIntegrations(data);
    } catch (error) {
      setIntegrations([]);
      throw error;
    } finally {
      setIsReady(true);
    }
  }, [workspace, api, isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const closeSettings = () => {
    documentTitle.set('Integrations');
    history.push(url);
  };

  const closeError = () => {
    if (!params.has('error')) {
      return;
    }

    params.delete('error');

    const query = params.toString();
    if (query.length > 0) {
      history.push(`${location.pathname}?${query}`);
    } else {
      history.push(location.pathname);
    }
  };

  if (!isReady) return <PageLoader />;
  if (!integrations.length) return <ErrorPage publicSite={false} />;

  return (
    <Page>
      <Page.Header>
        <Page.Info>
          <Page.Eyebrow>Settings</Page.Eyebrow>
          <Page.Title>Integrations</Page.Title>
        </Page.Info>
      </Page.Header>
      <Page.Section>
        <IntegrationsList title="Active Integrations" integrations={activeIntegrations} onChange={fetchData} />

        <IntegrationsList title="Available Integrations" integrations={availableIntegrations} onChange={fetchData} />
        <Switch>
          <Route path={`${path}/slack`} exact>
            <SlackSettings onClose={closeSettings} />
          </Route>
          <Route path={`${path}/microsoft`} exact>
            <MicrosoftTeamsSettings onClose={closeSettings} />
          </Route>
        </Switch>
        {error === 'slack-in-use' && <SlackError onClose={closeError} />}
      </Page.Section>
    </Page>
  );
}
